import { notificationsStore, type NotificationData } from '@mantine/notifications';
import { posthog } from './posthog';
import { analyticsSafeCode, errorCodeForTitle, type SendFailureCode } from './errorCodes';
import { normalizeRoutePath } from './routePath';

// 2026-08-10 UAT: every handled block (ramp-on-multi-product, PoC/paid mixing, the 422 send
// rejection) raised a toast and nothing else, so none of them existed in PostHog. Error toasts are
// the one surface every handled validation already goes through, so they are hooked centrally
// instead of at each of the ~150 notifications.show call sites.
//
// Nothing here forwards error text. Toast titles are mapped to allowlisted codes in errorCodes.ts
// and the message is dropped, because both toast messages and API error strings can carry customer
// or internal business data.
const ERROR_COLORS = new Set(['red', 'orange']);
const QUOTE_ID_PATTERN = /\/quotes\/([0-9a-fA-F-]{36})/;

let wizardStep: string | null = null;

export function setQuoteWizardStep(step: string | null) {
  wizardStep = step;
}

function currentQuoteId(): string | null {
  if (typeof window === 'undefined') return null;
  return QUOTE_ID_PATTERN.exec(window.location.pathname)?.[1] ?? null;
}

function currentPath(): string | null {
  if (typeof window === 'undefined') return null;
  // Deny-by-default: only known static route segments survive. The quote id travels as its own
  // property; no other path value belongs in analytics.
  return normalizeRoutePath(window.location.pathname);
}

// Which inline blocker a rep ran into, for the validation_error events raised by a locked control
// rather than by a toast. A closed set, checked again at capture time, so nothing but these literals
// can ride along.
export const VALIDATION_BLOCK_REASONS = [
  'submit-disabled',
  'pill-locked',
  'continue-blocked',
  'target-locked',
  'timing-locked',
] as const;

export type ValidationBlockReason = (typeof VALIDATION_BLOCK_REASONS)[number];

function blockReason(reason: unknown): ValidationBlockReason | null {
  return VALIDATION_BLOCK_REASONS.find((known) => known === reason) ?? null;
}

export interface ValidationErrorEvent {
  code: string;
  status?: number | null;
  surface?: string | null;
  quoteId?: string | null;
  reason?: ValidationBlockReason | null;
}

export function captureValidationError(event: ValidationErrorEvent) {
  posthog.capture('validation_error', {
    code: event.code,
    status: event.status ?? null,
    surface: event.surface ?? null,
    quoteId: event.quoteId ?? currentQuoteId(),
    wizardStep,
    reason: blockReason(event.reason),
    path: currentPath(),
  });
}

export interface QuoteWizardStepViewedEvent {
  step: string;
  quoteType: string | null;
  mode: 'new' | 'edit';
  isRestoredDraft: boolean;
}

export function captureQuoteWizardStepViewed(event: QuoteWizardStepViewedEvent) {
  posthog.capture('quote_wizard_step_viewed', {
    step: event.step,
    quoteType: event.quoteType,
    mode: event.mode,
    isRestoredDraft: event.isRestoredDraft,
    quoteId: currentQuoteId(),
  });
}

export type QuoteSendAction = 'send' | 'resend';

export function captureQuoteSendClicked(
  quoteId: string,
  surface: string,
  action: QuoteSendAction,
) {
  posthog.capture('quote_send_clicked', { quoteId, surface, action });
}

export function captureQuoteSendSucceeded(
  quoteId: string,
  surface: string,
  action: QuoteSendAction,
) {
  posthog.capture('quote_send_succeeded', { quoteId, surface, action });
}

export interface QuoteSendFailedEvent {
  quoteId: string;
  surface: string;
  action: QuoteSendAction;
  status: number | null;
  retryable: boolean;
  code: SendFailureCode;
}

export function captureQuoteSendFailed(event: QuoteSendFailedEvent) {
  posthog.capture('quote_send_failed', {
    quoteId: event.quoteId,
    surface: event.surface,
    action: event.action,
    status: event.status,
    retryable: event.retryable,
    code: event.code,
  });
}

export interface QuoteWizardOpenedFromDealEvent {
  src: string;
  outcome: string;
  newCustomer: boolean | null;
  hasExistingQuote: boolean | null;
}

export function captureQuoteWizardOpenedFromDeal(event: QuoteWizardOpenedFromDealEvent) {
  posthog.capture('quote_wizard_opened_from_deal', { ...event });
}

export interface AssistantBuildStartedEvent {
  dealId: string | null;
  buildId: string;
  force: boolean;
  reused: boolean;
  hasRepBrief: boolean;
  ignoreExistingQuotes: boolean;
  mode: 'deal' | 'prompt';
  includeDealEvidence: boolean;
}

export function captureAssistantBuildStarted(event: AssistantBuildStartedEvent) {
  posthog.capture('assistant_build_started', { ...event, path: currentPath() });
}

export interface AssistantBuildReadyEvent {
  dealId: string;
  buildId: string;
  questions: number;
  lines: number;
  confidenceHigh: number;
  confidenceMedium: number;
  confidenceLow: number;
  needsRepBrief: boolean;
  hasExistingQuote: boolean;
  mode: 'deal' | 'prompt';
}

export function captureAssistantBuildReady(event: AssistantBuildReadyEvent) {
  posthog.capture('assistant_build_ready', { ...event, path: currentPath() });
}

export interface AssistantBuildConfirmedEvent {
  dealId: string | null;
  buildId: string;
  quoteId: string;
  stripped: number;
  lowConfidence: number;
  overrides: number;
}

export function captureAssistantBuildConfirmed(event: AssistantBuildConfirmedEvent) {
  posthog.capture('assistant_build_confirmed', { ...event, path: currentPath() });
}

export interface AssistantBuildAbandonedEvent {
  dealId: string | null;
  buildId: string;
}

export function captureAssistantBuildAbandoned(event: AssistantBuildAbandonedEvent) {
  posthog.capture('assistant_build_abandoned', { ...event, path: currentPath() });
}

export interface AssistantFeedbackEvent {
  buildId: string;
  quoteId: string;
  dealId: string | null;
  rating: 'up' | 'down';
  reason: string | null;
  hasNote: boolean;
  saved: boolean;
}

export function captureAssistantFeedback(event: AssistantFeedbackEvent) {
  posthog.capture('assistant_feedback', { ...event, path: currentPath() });
}

export function captureOnboardingFeeAutoAdded(productFamily: string, optionTier: string) {
  posthog.capture('onboarding_fee_auto_added', { productFamily, optionTier });
}

export function captureOnboardingFeeWaived(productFamily: string) {
  posthog.capture('onboarding_fee_waived', { productFamily });
}

export function captureOnboardingOptionChanged(
  productFamily: string,
  fromTier: string | undefined,
  toTier: string,
) {
  posthog.capture('onboarding_option_changed', {
    productFamily,
    ...(fromTier === undefined ? {} : { fromTier }),
    toTier,
  });
}

// A rejected send carries a stable gate code from the server, but the toast is all the
// notification subscriber below ever sees. Registering the code against the notification id keeps
// one validation_error producer instead of a second capture path that would double-count: the
// subscriber prefers the tagged code and falls back to the title lookup, so `code` still has
// exactly one meaning and one source. Vetting happens here, on the way in, because this is the
// only door a server-supplied string can use to reach analytics.
const codesByNotificationId = new Map<string, string>();

export function tagNotificationCode(id: string, code: string | null) {
  const safe = analyticsSafeCode(code);
  if (safe) codesByNotificationId.set(id, safe);
}

function isErrorNotification(notification: NotificationData): boolean {
  return ERROR_COLORS.has(String(notification.color ?? ''));
}

let stop: (() => void) | null = null;

// Returns the unsubscribe so tests (and hot reload) can tear the subscription down. Calling it
// twice keeps a single subscription.
export function startNotificationAnalytics(): () => void {
  if (stop) return stop;

  let seen = new Set<string>();
  const unsubscribe = notificationsStore.subscribe((state) => {
    const open = [...state.notifications, ...state.queue];
    const nextSeen = new Set<string>();
    for (const notification of open) {
      const id = notification.id;
      if (!id) continue;
      nextSeen.add(id);
      if (seen.has(id) || !isErrorNotification(notification)) continue;
      captureValidationError({
        // The server's gate code is the more specific answer to "what refused this?"; the title
        // lookup is the fallback for every toast that has no code of its own.
        code: codesByNotificationId.get(id) ?? errorCodeForTitle(notification.title),
        surface: 'notification',
      });
    }
    // Tags outlive their toast when it closes, or when it never was an error toast at all (a 409
    // conflict shows blue). Dropping the ones whose notification is gone keeps the map bounded.
    for (const id of codesByNotificationId.keys())
      if (!nextSeen.has(id)) codesByNotificationId.delete(id);
    seen = nextSeen;
  });

  stop = () => {
    unsubscribe();
    stop = null;
  };
  return stop;
}
